<template>
  <BGModal :shown="dialogShown"
           :title="$t('admin_acc_facility_type.edit_title') + (itemType ? (` : ${itemType.name_en} ${itemType.name_zh}`) : '')"
           fixed-tabs>
    <template #content>
      <v-container class="edit-item-type-content pa-0" fluid v-if="itemType">
        <v-tabs fixed-tabs
                v-model="tabIndex">
          <v-tab>{{ $t('register.basic_info') }}</v-tab>
          <v-tab-item>
            <v-container class="overflow-y-scroll" fluid>
              <v-form ref="itemTypeForm">
                <v-text-field outlined
                              :label="$t('admin_acc_facility_type.name_en')"
                              v-model="itemType.name_en"
                              :rules="requiredRules"></v-text-field>
                <v-text-field outlined
                              :label="$t('admin_acc_facility_type.name_zh')"
                              v-model="itemType.name_zh"
                              :rules="requiredRules"></v-text-field>
                <v-input hide-details
                         v-model="itemType.description_en">
                  <v-container fluid class="flex-column">
                    <legend class="v-label theme--light">{{ $t('admin_acc_facility_type.description_en') }}</legend>
                    <TinyMceEditor v-model="itemType.description_en"></TinyMceEditor>
                  </v-container>
                </v-input>
                <v-input hide-details
                         v-model="itemType.description_zh">
                  <v-container fluid class="flex-column">
                    <legend class="v-label theme--light">{{ $t('admin_acc_facility_type.description_zh') }}</legend>
                    <TinyMceEditor v-model="itemType.description_zh"></TinyMceEditor>
                  </v-container>
                </v-input>
                <v-switch v-model="itemType.open_for_booking"
                          :label="$t('admin_acc_facility_type.open_for_booking')"></v-switch>
                <v-switch v-model="itemType.auto_assign_items"
                          :label="$t('admin_acc_facility_type.auto_assign_items')"></v-switch>
                <v-switch v-if="itemType.name_en !== 'Gymnasium'" v-model="itemType.skip_rest_of_the_day"
                          :label="$t('admin_acc_facility_type.skip_rest_of_the_day')"></v-switch>
                <v-input hide-details="auto"
                         v-model="itemType.icon">
                  <v-container fluid class="flex-column">
                    <legend class="v-label theme--light">{{ $t('admin_acc_facility_type.icon') }}</legend>
                    <PhotoPreviewInput v-model="itemType.icon"
                                       file-name="item-type-icon"></PhotoPreviewInput>
                    <small class="form-text text-muted">ACCEPTED FILE FORMAT: PNG, JPEG, JPG</small>
                  </v-container>
                </v-input>
                <v-input hide-details="auto"
                         v-model="itemType.banner"
                         :rules="requiredRules">
                  <v-container fluid class="flex-column">
                    <legend class="v-label theme--light">{{ $t('admin_acc_facility_type.banner') }}</legend>
                    <PhotoPreviewInput v-model="itemType.banner"
                                       file-name="item-type-banner"></PhotoPreviewInput>
                    <small class="form-text text-muted">ACCEPTED FILE FORMAT: PNG, JPEG, JPG; RECOMMENDED SIZE: <b>2545px(W) X 1081px(H)</b></small>
                  </v-container>
                </v-input>
                <v-input hide-details="auto"
                         v-model="itemType.photos"
                         :rules="requiredRules">
                  <v-container fluid class="flex-column">
                    <legend class="v-label theme--light">{{ $t('admin_acc_facility_type.photo') }}</legend>
                    <PhotoPreviewInput v-model="itemType.photos"
                                       file-name="item-type-photo"></PhotoPreviewInput>
                    <small class="form-text text-muted">ACCEPTED FILE FORMAT: PNG, JPEG, JPG; RECOMMENDED SIZE: <b>1013(W) X 1287(H)</b> (MEGA EVENT: <b>2371(W) X 787 (H)</b>)</small>
                  </v-container>
                </v-input>
              </v-form>
            </v-container>
          </v-tab-item>

          <v-tab>{{ $t('admin_acc_facility_type.price_tiers') }}</v-tab>
          <v-tab-item>
            <v-container class="overflow-y-scroll" fluid>
              <PriceTierList :type-id="itemTypeId"
                             v-if="tabIndex === 1"></PriceTierList>
            </v-container>
          </v-tab-item>

          <v-tab>{{ $t('admin_acc_facility_type.additional_items') }}</v-tab>
          <v-tab-item>
            <v-container fluid>
              <v-row class="flex-grow-0">
                <v-col class="text-right">
                  <v-btn depressed
                         tile
                         color="primary"
                         @click="addAdditionalItemModalShown = true">
                    <v-icon>fas fa-plus</v-icon>
                  </v-btn>
                </v-col>
              </v-row>
            </v-container>
            <ItemTypeAdditionalItemList full-height
                                        :item-type-id="itemTypeId"
                                        :refresh-trigger.sync="additionalItemRefreshTrigger"
                                        @edit="onAdditionalItemEdit"
                                        v-if="tabIndex === 2"></ItemTypeAdditionalItemList>

            <AddItemTypeAdditionalItemModal :shown="addAdditionalItemModalShown"
                                            :item-type-id="itemTypeId"
                                            @cancel="onAddAdditionalItemModalFinish"></AddItemTypeAdditionalItemModal>

            <EditItemTypeAdditionalItemModal :shown="editAdditionalItemModalShown"
                                             :additional-item-id="editingAdditionalItemId"
                                             @cancel="onEditAdditionalItemModalFinish"></EditItemTypeAdditionalItemModal>
          </v-tab-item>

          <v-tab>{{ $t('admin_acc_facility_type.day_schedule') }}</v-tab>
          <v-tab-item>
            <v-container class="overflow-y-scroll" fluid>
              <DayScheduleList :type-id="itemTypeId"
                               v-if="tabIndex === 3"></DayScheduleList>
            </v-container>
          </v-tab-item>

          <v-tab>{{ $t('admin_acc_facility_type.week_schedule') }}</v-tab>
          <v-tab-item>
            <v-container class="overflow-y-scroll" fluid>
              <WeekScheduleList :type-id="itemTypeId"
                                v-if="tabIndex === 4"></WeekScheduleList>
            </v-container>
          </v-tab-item>

          <v-tab>{{ $t('admin_acc_facility_type.adhoc_week_schedule') }}</v-tab>
          <v-tab-item>
            <v-container class="overflow-y-scroll" fluid>
              <AdhocWeekScheduleList :type-id="itemTypeId"
                                     v-if="tabIndex === 5"></AdhocWeekScheduleList>
            </v-container>
          </v-tab-item>

          <v-tab>{{ $t('admin_acc_facility_type.calendar') }}</v-tab>
          <v-tab-item>
            <v-container class="overflow-y-scroll" fluid>
              <ItemTypeCalendarView :type-id="itemTypeId"
                                    v-if="tabIndex === 6"></ItemTypeCalendarView>
            </v-container>
          </v-tab-item>

          <v-tab>{{ $t('admin_acc_item.title') }}</v-tab>
          <v-tab-item>
            <ItemList full-height
                      :type-id="itemTypeId"
                      :refresh-trigger.sync="itemRefreshTrigger"
                      @edit="onItemEdit"
                      v-if="tabIndex === 7"></ItemList>

            <EditItemModal :item-id="editingItemId"
                           :shown="editItemModalShown"
                           @cancel="onEditItemModalFinish"></EditItemModal>
          </v-tab-item>
        </v-tabs>
      </v-container>
    </template>
    <template #footer>
      <v-row no-gutters>
        <v-btn @click="cancel"
               class="mr-3"
               depressed
               large>
          {{ $t('cancel') }}
        </v-btn>
        <v-spacer></v-spacer>
        <v-btn color="primary"
               depressed
               large
               @click="$refs.itemTypeForm.validate() && saveItemType()"
               v-if="tabIndex === 0">
          {{ $t('save') }}
          <v-icon right>fas fa-check</v-icon>
        </v-btn>
      </v-row>
    </template>
  </BGModal>
</template>
<script>
import BGModal from "@/components/BGModal";
import {ref, watch} from "@vue/composition-api";
import TinyMceEditor from "@/components/TinyMceEditor";
import PhotoPreviewInput from "@/components/PhotoPreviewInput";
import PriceTierList from "@/components/acc_booking/PriceTierList";
import DayScheduleList from "@/components/acc_booking/item_type/DayScheduleList";
import WeekScheduleList from "@/components/acc_booking/item_type/WeekScheduleList";
import AdhocWeekScheduleList from "@/components/acc_booking/item_type/AdHocWeekScheduleList";
import ItemTypeCalendarView from "@/components/acc_booking/item_type/ItemTypeCalendarView";
import ItemList from "@/components/acc_booking/item/ItemList";
import EditItemModal from "@/components/acc_booking/item/EditItemModal";
import ItemTypeAdditionalItemList from "@/components/acc_booking/item_type/ItemTypeAdditionalItemList";
import AddItemTypeAdditionalItemModal from "@/components/acc_booking/item_type/AddItemTypeAdditonalItemModal";
import EditItemTypeAdditionalItemModal from "@/components/acc_booking/item_type/EditItemTypeAdditionalItemModal";

export default {
  name: 'EditItemTypeModal',
  components: {EditItemTypeAdditionalItemModal, AddItemTypeAdditionalItemModal, ItemTypeAdditionalItemList, EditItemModal, ItemList, DayScheduleList, WeekScheduleList, AdhocWeekScheduleList, ItemTypeCalendarView, PriceTierList, PhotoPreviewInput, TinyMceEditor, BGModal},
  props: {
    itemTypeId: String,
    shown: Boolean,
  },
  setup(props, {root, emit}) {
    const dialogShown = ref(props.shown);
    const cancel = function () {
      emit('cancel');
    };

    const tabIndex = ref(0);

    // Init
    watch(() => props.shown, async (newValue, oldValue) => {
      if (newValue !== oldValue) {
        dialogShown.value = newValue;

        if (newValue) {
          getItemType()
        }
      }

      tabIndex.value = 0;
    });

    const itemType = ref(null);
    const getItemType = function () {
      root.$callAPI('aafb/admin/item_type/', {type_id: props.itemTypeId}, function (response) {
        itemType.value = {
          ...response.body.item_type,
          // photos may be null for a freshly created facility -- an unguarded
          // JSON.parse(null)[0] throws and leaves the whole modal blank
          photos: response.body.item_type.photos ? JSON.parse(response.body.item_type.photos)[0] : null,
        };
      });
    }

    // Info
    const requiredRules = ref([
      value => !!value || root.$t('register.required_fields'),
    ]);
    const saveItemType = async function () {
      root.$callAPI('aafb/admin/item_type/set/', {
        ...itemType.value,
        type_id: props.itemTypeId,
        icon: itemType.value.icon instanceof File ? await root.$convertFileToBase64(itemType.value.icon) : itemType.value.icon,
        banner: itemType.value.banner instanceof File ? await root.$convertFileToBase64(itemType.value.banner) : itemType.value.banner,
        photos: itemType.value.photos instanceof File ? await root.$convertFileToBase64(itemType.value.photos) : itemType.value.photos,
      }, () => {
        root.$showAlertMessage('Info Saved');
      })
    }

    //
    const additionalItemRefreshTrigger = ref(false);
    const addAdditionalItemModalShown = ref(false);
    const onAddAdditionalItemModalFinish = function () {
      additionalItemRefreshTrigger.value = true;
      addAdditionalItemModalShown.value = false;
    }

    const editingAdditionalItemId = ref(null);
    const editAdditionalItemModalShown = ref(false);
    const onAdditionalItemEdit = function (item) {
      editingAdditionalItemId.value = item.id;
      editAdditionalItemModalShown.value = true;
    };
    const onEditAdditionalItemModalFinish = function () {
      additionalItemRefreshTrigger.value = true;
      editingAdditionalItemId.value = null;
      editAdditionalItemModalShown.value = false;
    }

    //
    const itemRefreshTrigger = ref(false);
    const editingItemId = ref(null);
    const editItemModalShown = ref(false);
    const onItemEdit = function (item) {
      editingItemId.value = item.id;
      editItemModalShown.value = true;
    };
    const onEditItemModalFinish = function () {
      itemRefreshTrigger.value = true;
      editingItemId.value = null;
      editItemModalShown.value = false;
    }


    return {
      dialogShown,
      cancel,

      tabIndex,

      itemType,

      // Info
      requiredRules,
      saveItemType,

      additionalItemRefreshTrigger,
      addAdditionalItemModalShown,
      onAddAdditionalItemModalFinish,
      editingAdditionalItemId,
      editAdditionalItemModalShown,
      onAdditionalItemEdit,
      onEditAdditionalItemModalFinish,

      itemRefreshTrigger,
      editingItemId,
      editItemModalShown,
      onItemEdit,
      onEditItemModalFinish,
    }
  }
}
</script>

<style lang="less">
.edit-item-type-content {
  display: flex;
  height: 100%;
}
</style>
